import { getCachedAuthToken } from '@/stores/authStore';
import type { WebsocketEvent } from '@/types/websocket';
import { handleError } from '@/utils/errorHandling';
import { encodeTwrn } from '@/utils/helpers';
import { debugLog } from '@/utils/logging';
import { handleUserChannelEvent, isEventKey } from '@/utils/websocket';
import { datadogLogs } from '@datadog/browser-logs';
import Pusher from 'pusher-js';

const log = (msg: string) => debugLog(msg, 'Pusher');

let pusherInstance: Pusher | null = null;

/**
 * Reports an async Pusher failure (connection/subscription errors surface as
 * events, not thrown exceptions, so they can't be caught by try/catch)
 */
const reportPusherError = (message: string, error: unknown) => {
  log(`${message} ⚠️`);
  datadogLogs.logger.error(`[Pusher] ${message}`, { error });
};

const getPusher = () => {
  if (pusherInstance === null) {
    const authToken = getCachedAuthToken();

    if (authToken !== null) {
      pusherInstance = new Pusher(import.meta.env.VITE_PUSHER_APP_KEY, {
        cluster: import.meta.env.VITE_PUSHER_APP_CLUSTER,
        ...(import.meta.env.VITE_PUSHER_HOST
          ? {
              wsHost: import.meta.env.VITE_PUSHER_HOST,
              wssPort: Number(import.meta.env.VITE_PUSHER_PORT || 443),
              enabledTransports: ['ws', 'wss'] as ('ws' | 'wss')[],
            }
          : {}),
        channelAuthorization: {
          endpoint: `${import.meta.env.VITE_API_BASE}/broadcasting/auth`,
          headersProvider: () => ({
            Authorization: `Bearer ${getCachedAuthToken()}`,
          }),
          transport: 'ajax',
        },
        forceTLS: true,
      });

      log('Pusher instance created ✅');

      pusherInstance.connection.bind('error', (error: unknown) => {
        reportPusherError('Connection error', error);
      });
    }
  } else {
    log('Pusher instance reused 🔃');
  }

  return pusherInstance;
};

/**
 * Subscribes to a channel
 * @param channelName
 * @returns The subscribed channel
 */
const subscribe = (
  channelName: string,
  eventHandler?: (key: string, event: WebsocketEvent) => void,
) => {
  try {
    pusherInstance = getPusher();
    if (!pusherInstance) throw new Error('No Pusher instance');

    const isSubbed = !!pusherInstance.channels.find(channelName);
    if (isSubbed) {
      log(`Already subscribed to ${channelName}`);
      return;
    }

    const channel = pusherInstance.subscribe(channelName);
    log(`Subscribed to ${channel.name}`);

    channel.bind('pusher:subscription_error', (status: unknown) => {
      reportPusherError(`Subscription error on ${channelName}`, status);
    });

    if (eventHandler) channel.bind_global((key: string, data: WebsocketEvent) => {
      if (isEventKey(key)) {
        eventHandler(key, data);
      } else if (key.startsWith('pusher:')) {
        // Silently ignore pusher messages for now
      } else {
        log(`Unknown event key: ${key}`);
      }
    });

    return channel;
  } catch (error) {
    handleError(error);
  }
};

/**
 * Subscribes a user to their private channel
 * @param userTwrn
 */
const subscribeToUserChannel = (userTwrn: string) => {
  const channelName = `private-${import.meta.env.VITE_AWS_REGION}.user.${encodeTwrn(userTwrn)}`;
  subscribe(channelName, handleUserChannelEvent);
};

/**
 * Disconnects the Pusher connection and clears the cached instance
 */
const disconnectPusher = () => {
  if (pusherInstance !== null) {
    pusherInstance.disconnect();
    pusherInstance = null;
    log('Disconnected');
  }
};

export { subscribeToUserChannel, disconnectPusher };
